import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  PrinterIcon,
  ShieldCheckIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

// Fictional examples of the business-wide diagnostic, not client findings.
const reportFindings = [
  {
    id: '1 · NOW', title: 'Connect accounting and service records',
    observation: 'The fictional coordinator enters the same job details in two systems.',
    implication: 'Reliable shared job data supports growth without proportional office overhead and enables earlier cost visibility.',
    recommendation: 'Operations manager: map shared fields and test one approved transfer. Illustrative one-off band: CAD $25,000–$75,000; recurring cost unverified. Confirm API access, ownership and error handling first.',
    evidence: 'Fictional coordinator answer. Measure weekly re-entry hours and mismatched job records.',
  },
  {
    id: '2 · NEXT', title: 'Make scheduling knowledge shared and usable',
    observation: 'The fictional dispatcher holds scheduling rules and crew qualifications informally.',
    implication: 'The business depends on one person to keep work moving. Shared rules and backup cover reduce that dependency.',
    recommendation: 'Operations manager: document qualifications and exceptions, then test shared scheduling. Illustrative one-off band: CAD $25,000–$75,000; recurring cost unverified. Clean the qualification register before choosing software.',
    evidence: 'Fictional operations answer. Measure conflicts, reassignments and successful cover during absence.',
  },
  {
    id: '3 · NEXT', title: 'Capture field hours while jobs are underway',
    observation: 'The fictional controller receives paper time records too late to correct a drifting job.',
    implication: 'Earlier cost visibility can support intervention, but the recoverable benefit has not been established.',
    recommendation: 'Controller: test mobile time capture with one crew. Illustrative one-off band: CAD $25,000–$75,000; recurring cost unverified. Depend on reliable job codes and practical foreman training.',
    evidence: 'Fictional controller answer. Measure days from work performed to cost visibility and correction effort.',
  },
];

interface AdvancedReportPreviewProps {
  onBack: () => void;
  onExit: () => void;
}

const PrintRules = createGlobalStyle`
  @media print {
    @page { size: Letter portrait; margin: 0; }
    html, body { width: 8.5in !important; margin: 0 !important; padding: 0 !important; overflow: visible !important; background: white !important; }
    body * { visibility: hidden !important; }
    #advanced-report-preview, #advanced-report-preview * { visibility: visible !important; }
    #advanced-report-preview {
      position: absolute !important;
      inset: 0 auto auto 0 !important;
      width: 8.5in !important;
      margin: 0 !important;
      border: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
    }
    #advanced-report-preview > header,
    #advanced-report-preview > section {
      position: relative !important;
      width: 8.5in !important;
      height: 11in !important;
      min-height: 0 !important;
      margin: 0 !important;
      padding: 0.48in 0.58in 0.42in !important;
      overflow: hidden !important;
      box-sizing: border-box !important;
      break-after: page;
      page-break-after: always;
    }
    #advanced-report-preview > header::after,
    #advanced-report-preview > section::after {
      content: attr(data-page);
      position: absolute;
      right: 0.58in;
      bottom: 0.22in;
      color: var(--davinci-stone);
      font-size: 8px;
      font-weight: 700;
      letter-spacing: 0.08em;
    }
    #advanced-report-preview > section { border-top: 10px solid var(--davinci-blue) !important; }
    #advanced-report-preview > footer { display: none !important; }

    #advanced-report-preview > section > h3 {
      margin-bottom: 8px !important;
      font-size: 24px !important;
      line-height: 1.14 !important;
    }

    #advanced-report-preview > section > p {
      margin-bottom: 16px !important;
      font-size: 10px !important;
      line-height: 1.48 !important;
    }

    #advanced-report-preview > section > p:first-child {
      margin-bottom: 7px !important;
      font-size: 8px !important;
    }

    #advanced-report-preview > section article {
      gap: 14px !important;
      padding: 13px 0 !important;
    }

    #advanced-report-preview > section article h4 {
      margin-bottom: 7px !important;
      font-size: 13px !important;
    }

    #advanced-report-preview > section article p {
      margin: 4px 0 !important;
      font-size: 9px !important;
      line-height: 1.42 !important;
    }

    #advanced-report-preview > section article small { font-size: 7.5px !important; }
    .advanced-screen-only { display: none !important; }
  }
`;

const PreviewShell = styled.section`
  width: min(1180px, 100%);
  margin: 0 auto;
`;

const PreviewToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;

  @media (max-width: 640px) { align-items: flex-start; flex-direction: column; }
`;

const TextButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 10px 14px;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  background: white;
  color: var(--color-accent-dark);
  font: inherit;
  font-weight: 700;
  cursor: pointer;

  svg { width: 18px; }
  &:hover { border-color: var(--color-accent-secondary); }
  &:focus-visible { outline: 3px solid rgba(11, 152, 206, 0.32); outline-offset: 2px; }
`;

const PreviewLabel = styled.div`
  color: var(--text-secondary);
  font-size: var(--fs-sm);
  text-align: right;

  strong {
    display: block;
    color: var(--color-accent-dark);
  }

  @media (max-width: 640px) { text-align: left; }
`;

const Report = styled.article`
  overflow: hidden;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 18px 55px rgba(20, 53, 98, 0.12);
`;

const Cover = styled.header`
  position: relative;
  min-height: 460px;
  padding: clamp(32px, 7vw, 84px);
  overflow: hidden;
  background: white;
  color: var(--davinci-grey);

  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto;
    height: 12px;
    background: var(--davinci-blue);
  }
`;

const CoverLogo = styled.img`
  width: 180px;
  margin: 24px 0 38px;
`;

const Confidential = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-md);
  padding-bottom: 18px;
  border-bottom: 1px solid var(--davinci-cloud);
  color: var(--davinci-stone);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const CoverTitle = styled.h1`
  position: relative;
  z-index: 1;
  max-width: 760px;
  margin: 64px 0 22px;
  color: var(--davinci-midnight);
  font-size: clamp(2.5rem, 7vw, 5rem);
  line-height: 1.02;
  letter-spacing: -0.045em;
`;

const CoverSubtitle = styled.p`
  position: relative;
  z-index: 1;
  max-width: 620px;
  color: var(--davinci-grey);
  font-size: var(--fs-lg);
  line-height: 1.55;
`;

const ReportSection = styled.section`
  padding: clamp(28px, 6vw, 68px);
  border-bottom: 1px solid var(--border-light);
`;

const SectionEyebrow = styled.p`
  margin-bottom: 10px;
  color: var(--color-accent-secondary);
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.11em;
  text-transform: uppercase;
`;

const SectionTitle = styled.h3`
  max-width: 780px;
  margin: 0 0 14px;
  color: var(--color-accent-dark);
  font-size: clamp(1.75rem, 4vw, 2.8rem);
  letter-spacing: -0.035em;
`;

const SectionIntro = styled.p`
  max-width: 760px;
  margin-bottom: 32px;
  color: var(--text-secondary);
  line-height: 1.7;
`;

const DecisionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border: 1px solid var(--border-default);

  @media (max-width: 820px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const DecisionCell = styled.div`
  min-height: 150px;
  padding: 22px;
  border-right: 1px solid var(--border-default);

  &:last-child { border-right: 0; }
  span { display: block; margin-bottom: 22px; color: var(--text-muted); font-size: 0.76rem; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
  strong { display: block; color: var(--color-accent-dark); font-size: 1.35rem; line-height: 1.25; }
  small { display: block; margin-top: 8px; color: var(--text-secondary); line-height: 1.45; }

  @media (max-width: 820px) {
    &:nth-child(2) { border-right: 0; }
    &:nth-child(-n + 2) { border-bottom: 1px solid var(--border-default); }
  }

  @media (max-width: 480px) {
    border-right: 0;
    border-bottom: 1px solid var(--border-default);
    &:last-child { border-bottom: 0; }
  }
`;

const Bridge = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 34px 0;
  border-top: 3px solid var(--color-accent-primary);

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    border-top: 0;
    border-left: 3px solid var(--color-accent-primary);
  }
`;

const BridgeStep = styled.div`
  position: relative;
  padding: 24px 22px 4px 0;

  &::before {
    content: '';
    position: absolute;
    top: -9px;
    left: 0;
    width: 15px;
    height: 15px;
    border: 4px solid white;
    border-radius: 50%;
    background: var(--color-accent-primary);
    box-shadow: 0 0 0 1px var(--color-accent-primary);
  }

  span { display: block; margin-bottom: 7px; color: var(--color-accent-secondary); font-size: 0.75rem; font-weight: 800; text-transform: uppercase; }
  strong { display: block; color: var(--color-accent-dark); line-height: 1.3; }
  p { margin: 8px 0 0; color: var(--text-secondary); font-size: 0.9rem; line-height: 1.55; }

  @media (max-width: 720px) {
    padding: 0 0 28px 24px;
    &::before { top: 2px; left: -9px; }
  }
`;

const FindingList = styled.div`
  display: grid;
  gap: 18px;
`;

const Finding = styled.article`
  display: grid;
  grid-template-columns: 70px 1fr;
  gap: 22px;
  padding: 26px 0;
  border-top: 1px solid var(--border-default);

  @media (max-width: 600px) { grid-template-columns: 1fr; gap: 10px; }
`;

const FindingId = styled.div`
  color: var(--color-accent-secondary);
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.08em;
`;

const FindingBody = styled.div`
  h4 { margin: 0 0 14px; color: var(--color-accent-dark); font-size: 1.25rem; }
  p { margin: 8px 0; color: var(--text-secondary); line-height: 1.62; }
  strong { color: var(--text-primary); }
  small { color: var(--text-muted); }
`;

const AlignmentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;

  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

const AlignmentCard = styled.div`
  padding: 22px;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  background: var(--color-secondary-lightest);

  svg { width: 24px; color: var(--color-accent-secondary); }
  h4 { margin: 14px 0 8px; color: var(--color-accent-dark); font-size: 1.08rem; line-height: 1.3; letter-spacing: -0.01em; }
  p { color: var(--text-secondary); font-size: 0.92rem; line-height: 1.55; }
`;

const Roadmap = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: var(--border-default);
  border: 1px solid var(--border-default);

  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

const RoadmapStage = styled.div`
  padding: 24px;
  background: white;

  span { color: var(--color-accent-secondary); font-size: 0.8rem; font-weight: 800; text-transform: uppercase; }
  h4 { margin: 12px 0; color: var(--color-accent-dark); font-size: 1.12rem; line-height: 1.3; letter-spacing: -0.01em; }
  ul { margin: 0; padding-left: 18px; color: var(--text-secondary); line-height: 1.65; }
`;

const ReportFooter = styled.footer`
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-md);
  padding: 26px clamp(28px, 6vw, 68px);
  background: var(--color-accent-dark);
  color: rgba(255, 255, 255, 0.76);
  font-size: 0.84rem;

  button {
    border: 0;
    background: transparent;
    color: white;
    font: inherit;
    font-weight: 800;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 4px;
  }
`;

const AdvancedReportPreview: React.FC<AdvancedReportPreviewProps> = ({ onBack, onExit }) => (
  <PreviewShell>
    <PrintRules />
    <PreviewToolbar className="advanced-screen-only">
      <TextButton type="button" onClick={onBack}>
        <ArrowLeftIcon aria-hidden="true" />
        Back to assessment details
      </TextButton>
      <ToolbarRight>
        <PreviewLabel>
          <strong>Illustrative business diagnostic</strong>
          Fictional data · abbreviated report format
        </PreviewLabel>
        <TextButton type="button" onClick={() => window.print()}>
          <PrinterIcon aria-hidden="true" />
          Print or save PDF
        </TextButton>
      </ToolbarRight>
    </PreviewToolbar>

    <Report id="advanced-report-preview">
      <Cover data-page="01">
        <CoverLogo src="/images/logos/logo-horizontal-1x.png" alt="Davinci AI Solutions" />
        <Confidential>
          <span>AI Opportunity &amp; Readiness</span>
          <span>Confidential · Illustrative</span>
        </Confidential>
        <CoverTitle>Business Opportunity Diagnostic</CoverTitle>
        <CoverSubtitle>
          A fictional example for Northstar Field Services: compare business-wide opportunities,
          prioritize investments and identify practical next steps. Your private report is based on your own answers.
        </CoverSubtitle>
      </Cover>

      <ReportSection data-page="02">
        <SectionEyebrow>01 · Executive decision brief</SectionEyebrow>
        <SectionTitle>Connect job information before expanding the team.</SectionTitle>
        <SectionIntro>
          In this fictional business, growth is constrained by duplicate entry, late job-cost information
          and dependence on one dispatcher. Connect existing accounting and service records first, then
          improve scheduling and field time capture. Keep the recently renewed accounting platform.
        </SectionIntro>
        <DecisionGrid>
          <DecisionCell><span>Business goal</span><strong>Grow with control</strong><small>Increase capacity without proportional office overhead</small></DecisionCell>
          <DecisionCell><span>First priority</span><strong>Reliable job data</strong><small>Integration before additional automation</small></DecisionCell>
          <DecisionCell><span>Evidence</span><strong>Fictional answers</strong><small>No client records or external research verified</small></DecisionCell>
          <DecisionCell><span>Decision</span><strong>Validate first</strong><small>Confirm baseline, access and supplier scope</small></DecisionCell>
        </DecisionGrid>
      </ReportSection>

      <ReportSection data-page="03">
        <SectionEyebrow>02 · Business goals and coverage</SectionEyebrow>
        <SectionTitle>Compare opportunities across twelve business areas.</SectionTitle>
        <SectionIntro>
          The full diagnostic considers strategy, products and services, marketing, sales, delivery,
          customer service, people, IT, finance, assets, resilience and suppliers. Missing evidence remains
          an unknown. This abbreviated example connects findings to three business goals.
        </SectionIntro>
        <Bridge aria-label="Business goals, evidence and first priority">
          <BridgeStep><span>Goal 1</span><strong>Grow without office overload</strong><p>Example: coordinators re-enter the same job details in two systems.</p></BridgeStep>
          <BridgeStep><span>Goal 2</span><strong>See job costs sooner</strong><p>Example: paper time records reach accounting after work has finished.</p></BridgeStep>
          <BridgeStep><span>Goal 3</span><strong>Reduce key-person dependence</strong><p>Example: dispatch rules and certification knowledge sit with one person.</p></BridgeStep>
          <BridgeStep><span>First priority</span><strong>Connect existing records</strong><p>Confirm field mapping and access before buying another platform.</p></BridgeStep>
        </Bridge>
      </ReportSection>

      <ReportSection data-page="04">
        <SectionEyebrow>03 · Prioritized opportunities</SectionEyebrow>
        <SectionTitle>Know what comes first and what it depends on.</SectionTitle>
        <SectionIntro>These fictional planning examples are not quotations, verified savings or established
          payback periods. Validate scope, recurring costs and recoverable benefit before investing.</SectionIntro>
        <FindingList>
          {reportFindings.map((finding) => (
            <Finding key={finding.id}>
              <FindingId>{finding.id}</FindingId>
              <FindingBody>
                <h4>{finding.title}</h4>
                <p><strong>Observation:</strong> {finding.observation}</p>
                <p><strong>Implication:</strong> {finding.implication}</p>
                <p><strong>Recommendation:</strong> {finding.recommendation}</p>
                <small>Evidence: {finding.evidence}</small>
              </FindingBody>
            </Finding>
          ))}
        </FindingList>
      </ReportSection>

      <ReportSection data-page="05">
        <SectionEyebrow>04 · Alternatives and unknowns</SectionEyebrow>
        <SectionTitle>Explain what to defer and what still needs proof.</SectionTitle>
        <AlignmentGrid>
          <AlignmentCard><CheckCircleIcon aria-hidden="true" /><h4>Defer platform replacement</h4><p>The fictional business recently renewed its accounting system. Verify integration options before paying to replace it.</p></AlignmentCard>
          <AlignmentCard><UserGroupIcon aria-hidden="true" /><h4>Address continuity now</h4><p>Name backup cover for dispatch and test the handover. Software alone does not remove dependence on one person.</p></AlignmentCard>
          <AlignmentCard><ShieldCheckIcon aria-hidden="true" /><h4>Validate assumptions</h4><p>Supplier capabilities, adoption, ongoing costs and recoverable benefit are unverified. No external market research was performed for this sample.</p></AlignmentCard>
        </AlignmentGrid>
      </ReportSection>

      <ReportSection data-page="06">
        <SectionEyebrow>05 · Sequenced action plan</SectionEyebrow>
        <SectionTitle>Use prerequisites to set the pace.</SectionTitle>
        <SectionIntro>These are sequencing horizons, not delivery commitments. Scope and dates follow validation with the people who own the work.</SectionIntro>
        <Roadmap>
          <RoadmapStage><span>Now · first six months</span><h4>Establish the foundation</h4><ul><li>Operations: measure re-entry and map job fields</li><li>IT: verify integration and access</li><li>Operations: document dispatch backup cover</li></ul></RoadmapStage>
          <RoadmapStage><span>Next · six to eighteen months</span><h4>Test the next priorities</h4><ul><li>Operations: trial shared scheduling</li><li>Controller: pilot field time capture</li><li>Compare results with the baseline before expanding</li></ul></RoadmapStage>
          <RoadmapStage><span>Later · review after evidence</span><h4>Reconsider deferred work</h4><ul><li>Revisit remaining constraints</li><li>Consider AI where interpretation is a bottleneck</li><li>Proceed, revise or stop based on observed outcomes</li></ul></RoadmapStage>
        </Roadmap>
      </ReportSection>

      <ReportSection data-page="07">
        <SectionEyebrow>06 · Put the report to work</SectionEyebrow>
        <SectionTitle>Leave with a next action and the questions to ask.</SectionTitle>
        <SectionIntro>
          Start by measuring the current process and checking the first dependency. Use the included
          results-review call to challenge the priorities and assumptions. You can take the report to
          your existing team or another supplier; implementing a Davinci project is optional.
        </SectionIntro>
        <AlignmentGrid>
          <AlignmentCard><DocumentTextIcon aria-hidden="true" /><h4>Measure first</h4><p>Record re-entry hours, scheduling conflicts and time-to-cost visibility. Do not add overlapping costs or treat them as guaranteed savings.</p></AlignmentCard>
          <AlignmentCard><ShieldCheckIcon aria-hidden="true" /><h4>Ask suppliers</h4><p>Request scope, recurring costs, access controls, failure handling and acceptance criteria before approving work.</p></AlignmentCard>
          <AlignmentCard><CheckCircleIcon aria-hidden="true" /><h4>Review the decision</h4><p>Agree an owner and a small first step. Revisit the plan when evidence, priorities or system constraints change.</p></AlignmentCard>
        </AlignmentGrid>
      </ReportSection>

      <ReportFooter>
        <span>Business-wide diagnostic · Fictional illustration · No client conclusions</span>
        <button type="button" onClick={onExit}>Return to assessment options</button>
      </ReportFooter>
    </Report>
  </PreviewShell>
);

export default AdvancedReportPreview;
